import React, { useCallback, useState } from 'react';

import type { UUID } from '@aline/common';
import { ButtonBase } from '@mui/material';
import { alpha } from '@mui/material/styles';
import clsx from 'clsx';
import { UserPlus } from 'lucide-react';
import { makeStyles } from 'tss-react/mui';

import { useCanAssignWorkflowRoles } from './assign-roles';
import { AssignRolesDialog } from './AssignRolesDialog';

const useStyles = makeStyles()(theme => ({
  link: {
    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}` },
    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
    borderRadius: 6,
    color: theme.palette.primary.main,
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 600,
    gap: 4,
    lineHeight: '16px',
    padding: '2px 6px',
    transition: 'background-color 0.15s ease',
  },
}));

interface AssignRolesButtonProps {
  dialogTitle: string;
  label: string;
  permissionIds: readonly UUID[];
  workflowId: UUID;
  className?: string;
}

/**
 * An inline text link that opens the dialog filling these roles. Nothing renders when there are
 * none, or when the viewer can't fill roles on this workflow. Clicks stay inside, so a clickable
 * row around it doesn't fire.
 */
export const AssignRolesButton = React.memo(function AssignRolesButton(
  props: AssignRolesButtonProps
): JSX.Element | null {
  const canAssign = useCanAssignWorkflowRoles(props.workflowId);
  if (!canAssign || !props.permissionIds.length) return null;
  return <AssignRolesTrigger {...props} />;
});

function AssignRolesTrigger({
  className,
  dialogTitle,
  label,
  permissionIds,
  workflowId,
}: AssignRolesButtonProps): JSX.Element {
  const { classes } = useStyles();
  const [open, setOpen] = useState(false);
  const stopPropagation = useCallback((event: React.MouseEvent) => event.stopPropagation(), []);
  const handleOpen = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setOpen(true);
  }, []);
  const handleClose = useCallback(() => setOpen(false), []);

  return (
    <>
      <ButtonBase className={clsx(classes.link, className)} onClick={handleOpen}>
        <UserPlus size={14} />
        {label}
      </ButtonBase>
      <span onClick={stopPropagation}>
        <AssignRolesDialog
          open={open}
          permissionIds={permissionIds}
          title={dialogTitle}
          workflowId={workflowId}
          onClose={handleClose}
        />
      </span>
    </>
  );
}
